import WarningIcon from '@mui/icons-material/Warning';
import { Alert, Box, Card, Skeleton, Typography } from '@mui/material';
import { useIntl } from 'react-intl';

import { ColumnsOnlyToolbar, DataGrid, DataGridLayout } from '@verantos-dev/shared-components';

import type { AnalyticalDatasetResultRow } from '../../api/types';
import { DATE_BETWEEN_FILTER_INPUT_CLASS } from '../../components/DateBetweenFilterInput';
import {
  RESULTS_DEFAULT_SORT_MODEL,
  type ResultsTabEmptyState,
  useAnalyticalDatasetResultsTab,
} from '../../hooks/useAnalyticalDatasetResultsTab';

// Centered empty-state card matching the gen-3 cohort detail tabs (outlined
// card + h5/body1 block — same surface as "Cohort not processed"). The page
// gutter (px 6 / py 3) comes from the tab body, so only the card lives here.
const ResultsEmptyStateCard = ({ id, title, message }: ResultsTabEmptyState) => (
  <Card variant="outlined" sx={{ p: 6, minWidth: 0 }} data-testid={id}>
    <Box
      sx={{
        minHeight: 280,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 1,
      }}
    >
      <Typography variant="h5">{title}</Typography>
      <Typography variant="body1" color="text.secondary">
        {message}
      </Typography>
    </Box>
  </Card>
);

export const AnalyticalDatasetResultsTab = () => {
  const { formatMessage } = useIntl();
  const {
    isTabLoading,
    emptyState,
    showStagedChangesWarning,
    mapServerColumns,
    useFetchResultsGridData,
    columnVisibilityModel,
    onColumnVisibilityModelChange,
  } = useAnalyticalDatasetResultsTab();

  if (isTabLoading) {
    // Profile (latestStatus) or column metadata (version variables + AV
    // names) not resolved yet — shimmer where the grid card will sit so the
    // grid never mounts with a partial header row.
    return (
      <Box
        sx={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
        data-testid="ad-results-tab-loading"
      >
        <Skeleton variant="rounded" sx={{ width: '100%', flex: 1, minHeight: 400 }} />
      </Box>
    );
  }

  if (emptyState) {
    return (
      <Box data-testid="ad-results-tab">
        <ResultsEmptyStateCard {...emptyState} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
      }}
      data-testid="ad-results-tab"
    >
      {/* Same staged-changes banner as the Definition tab — the displayed
          results predate the unsaved edits. */}
      {showStagedChangesWarning && (
        <Alert
          severity="warning"
          icon={<WarningIcon fontSize="inherit" />}
          sx={{ py: 0.5 }}
          data-testid="ad-results-staged-changes-warning"
        >
          {formatMessage({ id: 'analyticalDatasetDetail.definition.stagedChangesWarning' })}
        </Alert>
      )}
      {/* No maxHeight: the layout flexes to the tab body's height (its own
          400px minHeight stays as the floor) and the grid scrolls inside. */}
      <DataGridLayout id="ad-results-grid-wrapper" sx={{ width: '100%', flex: 1 }}>
        <DataGrid
          id="ad-results-grid"
          columns={mapServerColumns}
          getRowId={(row: AnalyticalDatasetResultRow) => String(row.patient_id)}
          mode="server"
          useFetchDataGridData={useFetchResultsGridData}
          getRowCount={(data) => data.pagination.total}
          pagination
          loadOnFetch
          disableSearchParamSync
          // Opt-in shared-grid behaviors for server-side filtering; only this
          // grid enables them, so other grids keep their legacy behavior.
          // Multi-column filtering stays on — the backend ANDs/ORs all
          // comma-joined `field:operator(value)` items.
          resetPageOnFilterChange
          serializeDateFilterValues
          initialPageSize={500}
          pageSizeOptions={[25, 50, 100, 250, 500]}
          // Patient ID isn't pinned — it's the first server column so it sits
          // leftmost naturally (users can pin via the column menu); it just
          // can't be hidden (hideable: false in mapServerColumns). Default
          // sort reflects the server's stable patient_id ascending order.
          initialState={{
            sorting: { sortModel: RESULTS_DEFAULT_SORT_MODEL },
          }}
          // Demographics hidden by default when the dataset has AVs; user
          // toggles from the columns panel take over once made.
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={onColumnVisibilityModelChange}
          // Boolean filter Value dropdown labels, matching the title-cased
          // cell rendering (True/False). Display-only — the filter item value
          // (and thus the serialized wire format) stays true/false.
          localeText={{
            filterValueAny: 'Any',
            filterValueTrue: 'True',
            filterValueFalse: 'False',
          }}
          slots={{ toolbar: ColumnsOnlyToolbar }}
          slotProps={{
            // Cap every grid select menu (notably the filter form's Columns
            // dropdown — with many AVs it otherwise grows to the viewport).
            // ~8 rows visible, the rest scroll.
            // TODO: no VDS token for popover max-height — needs designer input
            baseSelect: {
              MenuProps: {
                PaperProps: { sx: { maxHeight: 320 } },
              },
            },
            // Align the columns panel's rows + footer with the search field's
            // inset: MUI's FormControlLabel (-11px margin) and the checkbox
            // ripple padding pull them left of the search border by default.
            panel: {
              sx: {
                // Filter panel: line the rows up as a table — consistent field
                // widths across rows (the hidden And/Or slot on row 1 already
                // reserves its column), padded rows, aligned baselines.
                '& .MuiDataGrid-filterForm': {
                  padding: 3,
                  gap: 3,
                  alignItems: 'flex-end',
                },
                '& .MuiDataGrid-filterFormLogicOperatorInput': { minWidth: 64 },
                '& .MuiDataGrid-filterFormColumnInput': { width: 200 },
                '& .MuiDataGrid-filterFormOperatorInput': { width: 150 },
                '& .MuiDataGrid-filterFormValueInput': { width: 200 },
                // The two-date `between` input needs room for both fields.
                [`& .MuiDataGrid-filterFormValueInput:has(.${DATE_BETWEEN_FILTER_INPUT_CLASS})`]: {
                  width: 320,
                },
                '& .MuiDataGrid-panelFooter': {
                  paddingLeft: 3,
                  paddingRight: 3,
                  paddingBottom: 2,
                },
                // Shared 20px horizontal inset for the search bar, the column
                // rows, and the footer.
                '& .MuiDataGrid-columnsManagementHeader': {
                  paddingLeft: 5,
                  paddingRight: 5,
                  paddingTop: 4,
                },
                '& .MuiDataGrid-columnsManagement': { paddingLeft: 5, paddingRight: 5 },
                '& .MuiDataGrid-columnsManagementRow': { marginLeft: 0, marginRight: 0 },
                '& .MuiDataGrid-columnsManagementRow .MuiCheckbox-root': { paddingLeft: 0 },
                '& .MuiDataGrid-columnsManagementFooter .MuiFormControlLabel-root': {
                  marginLeft: 0,
                },
                '& .MuiDataGrid-columnsManagementFooter .MuiCheckbox-root': { paddingLeft: 0 },
                '& .MuiDataGrid-columnsManagementFooter': {
                  paddingLeft: 5,
                  paddingRight: 5,
                  paddingTop: 4,
                  paddingBottom: 4,
                },
              },
            },
          }}
        />
      </DataGridLayout>
    </Box>
  );
};
